import { useCallback, useMemo, useState } from 'react';

import {
  ChakraFlex,
  Counter,
  Text,
  Tooltip,
  Icon,
  IconSize,
  FAIconName,
} from '@equip.health/ui';
import { ProviderAssignmentSelectionTab } from './constants';

interface Tab {
  count: number;
  label: ProviderAssignmentSelectionTab;
}
interface TabButtonProps {
  id: string;
  isActive: boolean;
  onSelect: (tab: Tab) => void;
  tab: Tab;
}

const TabButton = ({ id, isActive, onSelect, tab }: TabButtonProps) => {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(tab);
      }
    },
    [onSelect, tab],
  );

  const tabId = `${id}__${tab.label.toLowerCase().split(' ').join('-')}-tab`;

  return (
    <ChakraFlex
      alignItems="center"
      aria-selected={isActive}
      backgroundColor={isActive ? 'secondary.10' : undefined}
      borderRadius="12px"
      cursor="pointer"
      gap="6px"
      onClick={() => onSelect(tab)}
      onKeyDown={onKeyDown}
      padding="8px 16px"
      role="tab"
      tabIndex={0}
    >
      <Text
        color={isActive ? 'accent.blue' : 'black'}
        id={`${tabId}-label`}
        variant="bodyMedium"
      >
        {tab.label}
      </Text>
      <Counter id={tabId} isActive={isActive} number={tab.count} />
    </ChakraFlex>
  );
};

interface TabsProps {
  availabilityTooltipMessage?: string;
  defaultTab?: ProviderAssignmentSelectionTab;
  headerContent?: React.ReactNode;
  id: string;
  onTabChange?: (tab: ProviderAssignmentSelectionTab) => void;
  renderTabContent: (tab: ProviderAssignmentSelectionTab) => JSX.Element;
  tieredProviderMap: Record<
    ProviderAssignmentSelectionTab,
    Record<string, GetProviderByIdResponse>
  >;
}

const Tabs = ({
  availabilityTooltipMessage,
  defaultTab,
  headerContent,
  id,
  onTabChange,
  renderTabContent,
  tieredProviderMap,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState<ProviderAssignmentSelectionTab>(
    defaultTab ?? ProviderAssignmentSelectionTab.STRONG,
  );

  const handleTabSelect = useCallback(
    ({ label }: Tab) => {
      setActiveTab(label);
      onTabChange?.(label);
    },
    [onTabChange],
  );

  const getCount = useCallback(
    (tab: ProviderAssignmentSelectionTab) =>
      Object.keys(tieredProviderMap[tab] ?? {}).length,
    [tieredProviderMap],
  );

  const tabs: Tab[] = useMemo(
    () => [
      {
        count: getCount(ProviderAssignmentSelectionTab.STRONG),
        label: ProviderAssignmentSelectionTab.STRONG,
      },
      {
        count: getCount(ProviderAssignmentSelectionTab.REASONABLE),
        label: ProviderAssignmentSelectionTab.REASONABLE,
      },
      {
        count: getCount(ProviderAssignmentSelectionTab.LOW),
        label: ProviderAssignmentSelectionTab.LOW,
      },
    ],
    [getCount],
  );

  const activeTabContent = useMemo(
    () => renderTabContent(activeTab),
    [activeTab, renderTabContent],
  );

  return (
    <ChakraFlex direction="column" gap="24px">
      {headerContent}
      <ChakraFlex alignItems="center" gap="8px">
        <ChakraFlex gap="8px" role="tablist">
          {tabs.map((tab) => (
            <TabButton
              id={id}
              isActive={activeTab === tab.label}
              key={tab.label}
              onSelect={handleTabSelect}
              tab={tab}
            />
          ))}
        </ChakraFlex>
        {availabilityTooltipMessage && (
          <Tooltip
            id="provider-availability-tooltip"
            placement="top-start"
            tooltipText={availabilityTooltipMessage}
          >
            <Icon
              aria-hidden={false}
              aria-label="Provider availability information"
              color="neutral.secondary"
              name={FAIconName.infoCircleRegular}
              role="img"
              size={IconSize.medium}
            />
          </Tooltip>
        )}
      </ChakraFlex>
      {activeTabContent}
    </ChakraFlex>
  );
};

export default Tabs;
